import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    forwardRef,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    SimpleChanges,
} from "@angular/core"
import {
    AbstractControl,
    ControlValueAccessor,
    FormBuilder,
    FormControl,
    NG_VALIDATORS,
    NG_VALUE_ACCESSOR,
    ValidationErrors,
    Validator,
    Validators,
} from "@angular/forms"
import { takeUntil } from "rxjs/operators"
import { ReplaySubject } from "rxjs"

@Component({
    selector: "app-full-name",
    templateUrl: "./full-name.component.html",
    styleUrls: ["./full-name.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => FullNameComponent),
            multi: true,
        },
        {
            provide: NG_VALIDATORS,
            multi: true,
            useExisting: forwardRef(() => FullNameComponent),
        },
    ],
    standalone: false,
})
export class FullNameComponent
    implements OnInit, OnChanges, OnDestroy, ControlValueAccessor, Validator
{
    fullName = this.fb.group({
        firstName: ["", Validators.required],
        lastName: [""],
    })

    private destroyed$: ReplaySubject<boolean> = new ReplaySubject(1)
    private onTouched: () => void = () => {}
    private onValidatorChange: () => void = () => {}

    @Input() hasPublicMediaAccount: boolean

    constructor(
        private fb: FormBuilder,
        private changeDetectorRef: ChangeDetectorRef
    ) {}

    ngOnInit(): void {
        this.cleanInputsOnChanges()
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes["hasPublicMediaAccount"]) {
            if (this.hasPublicMediaAccount) {
                this.fullName.disable()
            } else {
                this.fullName.enable()
            }
            this.onValidatorChange()
        }
    }

    ngOnDestroy(): void {
        this.destroyed$.next()
        this.destroyed$.complete()
    }

    get firstName(): FormControl {
        return this.fullName.get("firstName") as FormControl
    }

    get lastName(): FormControl {
        return this.fullName.get("lastName") as FormControl
    }

    writeValue(value: { firstName: string; lastName: string }): void {
        this.fullName.patchValue(value, { emitEvent: false })
        this.changeDetectorRef.markForCheck()
    }

    validate(control: AbstractControl): ValidationErrors | null {
        if (this.fullName.disabled || this.fullName.valid) return null
        return { error: { message: "Please enter your first name" } }
    }

    registerOnChange(fn: any): void {
        this.fullName.valueChanges.pipe(takeUntil(this.destroyed$)).subscribe(fn)
    }

    registerOnTouched(fn: any): void {
        this.onTouched = fn
    }

    registerOnValidatorChange(fn: () => void): void {
        this.onValidatorChange = fn
    }

    private cleanInputsOnChanges(): void {
        this.firstName.valueChanges.pipe(takeUntil(this.destroyed$)).subscribe((data) => {
            this.firstName.setValue(this.cleanInput(data), { emitEvent: false })
        })

        this.lastName.valueChanges.pipe(takeUntil(this.destroyed$)).subscribe((data) => {
            this.lastName.setValue(this.cleanInput(data), { emitEvent: false })
        })
    }

    private cleanInput(inputValue): string {
        return inputValue.replace(/[^a-zA-Z0-9 -]/g, "")
    }
}
